// src/components/PerformancePanel.jsx
// 성과(Performance) 탭 — 실제 입출금·일별손익 기반 NAV 기준가 곡선 (TWR).
import React, { useMemo } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
} from "recharts";
import { computeNav } from "../lib/navCurve";
import RiskMetricsPanel from "./RiskMetricsPanel";
import PerfBenchmark from "./PerfBenchmark";

function pct(n, d = 1) {
  if (n == null || isNaN(n)) return "—";
  return `${n >= 0 ? "+" : ""}${(n * 100).toFixed(d)}%`;
}
function num(n, d = 2) {
  if (n == null || isNaN(n)) return "—";
  return n.toFixed(d);
}

export default function PerformancePanel({ holdings = [], marketData = {}, exchangeRates = {} }) {
  const navResult = useMemo(() => computeNav(), []);
  return (
    <div>
      <PerfBenchmark />
      <RiskMetricsPanel holdings={holdings} marketData={marketData} exchangeRates={exchangeRates} />
      {navResult ? <NavSection r={navResult} /> : (
        <div style={{ textAlign: "center", padding: "40px 20px", color: "#aaa", background: "#fff", border: "1px solid #e8e8e8", borderRadius: 12 }}>
          <div style={{ fontSize: 32, marginBottom: 10 }}>📈</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#888" }}>실현 NAV 성과 데이터 없음</div>
          <div style={{ fontSize: 12, marginTop: 6 }}>운용 시작 후 일별 NAV가 쌓이면 기준가 곡선이 표시됩니다.</div>
        </div>
      )}
    </div>
  );
}

function NavSection({ r }) {
  if (!r) return null;
  const m = r.metrics;
  const cards = [
    { label: "누적수익률", value: pct(m.totalRet), sub: `${m.start} ~ ${m.end}`, color: m.totalRet >= 0 ? "#0a7c59" : "#c0392b" },
    { label: "최종 기준가", value: m.finalPrice.toFixed(1), sub: "시작 1,000", color: "#1a1a2e" },
    { label: "연환산수익률", value: pct(m.annRet), sub: `${m.n}거래일 환산`, color: m.annRet >= 0 ? "#0a7c59" : "#c0392b" },
    { label: "Sharpe", value: num(m.sharpe), sub: "무위험 3%", color: (m.sharpe ?? 0) >= 1 ? "#0a7c59" : "#1a1a2e" },
    { label: "연환산 변동성", value: pct(m.vol), sub: "낮을수록 안정", color: "#1a1a2e" },
    { label: "최대낙폭 (MDD)", value: pct(m.mdd), sub: "고점 대비", color: "#c0392b" },
    { label: "목표(연7%) 대비", value: pct(m.excessRet), sub: `목표 ${pct(m.targetRet)}`, color: m.excessRet >= 0 ? "#0a7c59" : "#c0392b" },
  ];
  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: "#1a1a2e" }}>NAV 기준가 · 실제 운용성과</h3>
        <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
          실제 입출금·일별손익 기반 기준가(TWR) · 자금 유출입은 좌수로 흡수 → 순수 운용성과만 반영 · {m.start} = 1,000
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 16 }}>
        {cards.map(c => (
          <div key={c.label} style={{ background: "#fff", border: "1px solid #e8e8e8", borderRadius: 12, padding: "14px 16px", borderTop: `3px solid ${c.color}` }}>
            <div style={{ fontSize: 10.5, color: "#888", marginBottom: 6, letterSpacing: "0.03em", textTransform: "uppercase" }}>{c.label}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: c.color, lineHeight: 1.2 }}>{c.value}</div>
            <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>{c.sub}</div>
          </div>
        ))}
      </div>

      <div style={{ background: "#fff", border: "1px solid #e8e8e8", borderRadius: 12, padding: "20px 16px 8px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0 6px", marginBottom: 8 }}>
          <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#1a1a2e" }}>기준가 추이</h4>
          <div style={{ display: "flex", gap: 14 }}>
            <Legend color="#1a4d8f" label="기준가" />
            <Legend color="#e0a020" label="목표 +7%" />
          </div>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={r.chart} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#aaa" }} minTickGap={40} tickLine={false} axisLine={{ stroke: "#eee" }} tickFormatter={d => d.slice(5)} />
            <YAxis tick={{ fontSize: 10, fill: "#aaa" }} width={48} tickLine={false} axisLine={false} domain={["auto", "auto"]} tickFormatter={v => v.toFixed(0)} />
            <ReferenceLine y={1000} stroke="#ccc" strokeDasharray="4 4" />
            <Tooltip formatter={(v, name) => [v.toFixed(1), name === "target" ? "목표(7%)" : "기준가"]} labelStyle={{ fontSize: 12, color: "#555" }} contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e8e8e8" }} />
            <Line type="monotone" dataKey="target" stroke="#e0a020" strokeWidth={1.5} strokeDasharray="5 4" dot={false} />
            <Line type="monotone" dataKey="price" stroke="#1a4d8f" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
        <div style={{ fontSize: 10.5, color: "#bbb", textAlign: "right", padding: "4px 8px" }}>
          현재 평가액 {(m.finalNav / 1e8).toFixed(0)}억 · 입출금 반영 실현성과
        </div>
      </div>
    </div>
  );
}

function Legend({ color, label }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: "#777" }}>
      <span style={{ width: 14, height: 3, borderRadius: 2, background: color, display: "inline-block" }} />
      {label}
    </span>
  );
}
